import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { useEffect, useState } from "react";
import FilePicker from "../../components/file/FilePicker";
import { useFilePicker } from "../../hooks/file/useFilePicker";
import { useToast } from "../../hooks/useToast";
import { useGoogleDrive } from "./hooks/useGoogleDrive";
import { Collections } from "../../types/type";
import { useFirestoreBackup } from "./hooks/useFirestoreBackup";
import AudioPlayer from "./AudioPlayer";
import InferComponent from "./InferComponent";
import RvcModelFetcher from "./RvcModelFetcher";

const TestPage3: React.FC = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [backupProgressMessage, setBackupProgressMessage] =
    useState<string>("대기중");
  const [fetchedFileList, setFetchedFileList] = useState<any[]>([]);

  const { selectedFile, handleFileSelect } = useFilePicker();
  const { fireToast } = useToast();
  const { fetchFileListFromDrive, uploadFileToDrive, makeDirectory } =
    useGoogleDrive({
      accessToken,
    });
  const { exportCollectionsToJSON } = useFirestoreBackup();

  const handleGoogleLogin = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    // provider.addScope("https://www.googleapis.com/auth/drive");
    provider.addScope("https://www.googleapis.com/auth/drive");

    try {
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential?.accessToken;
      setAccessToken(token || null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return fireToast({ content: "파일 업로드 먼저" });

    try {
      const result = await uploadFileToDrive({
        file: selectedFile,
        directory: "backup",
      });
      if (result) fireToast({ content: "업로드 성공" });
    } catch (error) {
      console.error(error);
      fireToast({ content: "업로드 실패" });
    }
  };

  const generateYYMMDD = (): string => {
    // 현재 날짜로 디렉토리 이름 생성 (YYMMDD)
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yy}${mm}${dd}`;
  };

  const handleFileFetch = async () => {
    try {
      const result = await fetchFileListFromDrive();
      if (result) {
        setFetchedFileList(result);
        fireToast({ content: "fetch 성공" });
      }
    } catch (error) {
      console.error(error);
      fireToast({ content: "handleFileFetch 에러" });
    }
  };

  const handleBackupFirestoreData = async () => {
    if (!accessToken) {
      fireToast({ content: "구글 로그인 필요" });
      return;
    }

    let allUploadsSuccessful = true;

    try {
      // 백업할 컬렉션 이름 배열
      setBackupProgressMessage("백업 진행중");
      const collectionNameList = Object.values(Collections);

      // Firestore 데이터를 JSON으로 내보내기
      const dataArray = await exportCollectionsToJSON(collectionNameList);

      // 현재 날짜로 디렉토리 이름 생성 (YYMMDD)
      const YYMMDD = generateYYMMDD();
      const directoryName = `${YYMMDD}`;

      const backupFolderId = await makeDirectory("backup");
      if (!backupFolderId) {
        fireToast({ content: "backup 폴더 생성 또는 접근 실패" });
        return;
      }

      const dateFolderId = await makeDirectory(directoryName, backupFolderId);
      if (!dateFolderId) {
        fireToast({ content: `${directoryName} 폴더 생성 또는 접근 실패` });
        return;
      }

      for (const { collectionName, jsonData } of dataArray) {
        // JSON 문자열로부터 Blob 생성
        const blob = new Blob([jsonData], { type: "application/json" });

        // Blob으로부터 File 객체 생성
        const file = new File([blob], `${collectionName}.json`, {
          type: "application/json",
        });

        const result = await uploadFileToDrive({
          file,
          directory: directoryName,
          parentDirectoryId: backupFolderId,
        });

        if (result) {
          fireToast({ content: `${collectionName} 백업 및 업로드 성공` });
        } else {
          fireToast({ content: `${collectionName} 업로드 실패` });
          setBackupProgressMessage("백업 실패");
        }
      }

      if (allUploadsSuccessful) {
        setBackupProgressMessage("백업이 성공적으로 완료되었습니다.");
        fireToast({ content: "모든 컬렉션 백업 성공" });
      } else {
        setBackupProgressMessage("백업 중 일부 파일이 업로드되지 않았습니다.");
        fireToast({ content: "백업 일부 실패" });
      }
    } catch (error) {
      console.error(error);
      setBackupProgressMessage("백업 에러");
      fireToast({ content: "백업 실패" });
    }
  };

  return (
    <div>
      {/* <RvcModelFetcher /> */}
      <InferComponent />

      <h1>Google Drive API 연동</h1>
      <div style={{ marginBottom: 20 }}>{accessToken}</div>
      {!accessToken ? (
        <button onClick={handleGoogleLogin}>Google 로그인</button>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex" }}>
              <h3>파일 업로드</h3>
              <p>{selectedFile?.name || "파일 없음"}</p>
            </div>
            <div style={{ display: "flex" }}>
              <FilePicker onFileSelect={handleFileSelect} />
              <button onClick={handleFileUpload}>파일 업로드</button>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div>
              <h3>Firestore 백업 : {backupProgressMessage}</h3>
              <button onClick={handleBackupFirestoreData}>
                Firestore 백업
              </button>
            </div>
          </div>
          <div>
            <h3>파일 Fetch : {fetchedFileList.length}개</h3>
            <button onClick={handleFileFetch}>파일 Fetch</button>
            <ul>
              {fetchedFileList.map((file) => (
                <li key={file.id}>{file.name}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default TestPage3;
